import { useEffect, useState } from "react";
import { Dialog } from "@headlessui/react";
import {
  CheckCircleIcon,
  ExclamationTriangleIcon,
} from "@heroicons/react/24/outline";

import Modal from "../../../../layouts/Modal";

export interface ImportLeadOutcome {
  ccid?: string;
  vrm?: string;
  name?: string;
  reason: string;
  message?: string;
}

export interface ImportLeadsResultData {
  successful: ImportLeadOutcome[];
  unsuccessful: ImportLeadOutcome[];
}

export interface ImportLeadsResultModalProps {
  open: boolean;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
  result: ImportLeadsResultData | null;
}

type TabKey = "unsuccessful" | "successful";

const reasonBadgeClasses = (reason: string): string => {
  switch (reason?.toLowerCase()) {
    case "duplicate":
      return "bg-yellow-100 text-yellow-800";
    case "error":
      return "bg-red-100 text-red-800";
    case "imported":
      return "bg-green-100 text-green-800";
    default:
      return "bg-gray-100 text-gray-800";
  }
};

function OutcomeTable({
  outcomes,
  showReason,
}: {
  outcomes: ImportLeadOutcome[];
  showReason: boolean;
}) {
  if (outcomes.length === 0) {
    return (
      <p className="py-8 text-center text-sm text-gray-500">Nothing to show.</p>
    );
  }

  return (
    <div className="max-h-96 overflow-y-auto">
      <table className="min-w-full divide-y divide-gray-200">
        <thead className="sticky top-0 bg-gray-50">
          <tr>
            <th className="px-3 py-2 text-left text-xs font-medium uppercase tracking-wide text-gray-500">
              CCID
            </th>
            <th className="px-3 py-2 text-left text-xs font-medium uppercase tracking-wide text-gray-500">
              VRM
            </th>
            <th className="px-3 py-2 text-left text-xs font-medium uppercase tracking-wide text-gray-500">
              Name
            </th>
            {showReason && (
              <th className="px-3 py-2 text-left text-xs font-medium uppercase tracking-wide text-gray-500">
                Reason
              </th>
            )}
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100">
          {outcomes.map((o, i) => (
            <tr key={`${o.ccid ?? o.vrm ?? "lead"}-${i}`}>
              <td className="px-3 py-2 text-sm text-gray-700">{o.ccid || "—"}</td>
              <td className="px-3 py-2 text-sm font-medium text-gray-900">
                {o.vrm || "—"}
              </td>
              <td className="px-3 py-2 text-sm text-gray-700">{o.name || "—"}</td>
              {showReason && (
                <td className="px-3 py-2 text-sm">
                  <span
                    className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${reasonBadgeClasses(
                      o.reason
                    )}`}
                  >
                    {o.reason}
                  </span>
                  {o.message && (
                    <span className="ml-2 text-xs text-gray-500">{o.message}</span>
                  )}
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function ImportLeadsResultModal({
  open,
  setOpen,
  result,
}: ImportLeadsResultModalProps) {
  const unsuccessful = result?.unsuccessful ?? [];
  const successful = result?.successful ?? [];

  // Default to the tab most likely to need attention.
  const [activeTab, setActiveTab] = useState<TabKey>("unsuccessful");

  // A new result arrives after mount, so pick the sensible default each time.
  useEffect(() => {
    if (result) {
      setActiveTab(unsuccessful.length > 0 ? "unsuccessful" : "successful");
    }
  }, [result]); // eslint-disable-line react-hooks/exhaustive-deps

  const tabBtn = (key: TabKey, active: boolean) =>
    `flex items-center gap-1.5 border-b-2 px-4 py-2 text-sm font-medium focus:outline-none ${
      active
        ? "border-primary-600 text-primary-600"
        : "border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700"
    }`;

  return (
    <Modal open={open} setOpen={setOpen} width="sm:max-w-3xl">
      <div>
        <Dialog.Title
          as="h3"
          className="text-lg font-medium leading-6 text-gray-900"
        >
          Import Results
        </Dialog.Title>

        <div className="mt-4 border-b border-gray-200">
          <nav className="-mb-px flex space-x-2" aria-label="Tabs">
            <button
              type="button"
              className={tabBtn("unsuccessful", activeTab === "unsuccessful")}
              onClick={() => setActiveTab("unsuccessful")}
            >
              <ExclamationTriangleIcon className="h-4 w-4" aria-hidden="true" />
              Unsuccessful ({unsuccessful.length})
            </button>
            <button
              type="button"
              className={tabBtn("successful", activeTab === "successful")}
              onClick={() => setActiveTab("successful")}
            >
              <CheckCircleIcon className="h-4 w-4" aria-hidden="true" />
              Successful ({successful.length})
            </button>
          </nav>
        </div>

        <div className="mt-4">
          {activeTab === "unsuccessful" ? (
            <OutcomeTable outcomes={unsuccessful} showReason={true} />
          ) : (
            <OutcomeTable outcomes={successful} showReason={false} />
          )}
        </div>
      </div>

      <div className="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse">
        <button
          type="button"
          className="mt-3 inline-flex w-full justify-center rounded-md border border-gray-300 bg-white px-4 py-2 text-base font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 sm:mt-0 sm:w-auto sm:text-sm"
          onClick={() => setOpen(false)}
        >
          Close
        </button>
      </div>
    </Modal>
  );
}
